import { CatalystInsight, ThemeExposure, ThemePerformanceBasket } from '@api';
import { SmartTableColumn } from '@design/components/smart-table';
import { WithNAFallback } from '@design/components/table';
import { getPriceStatus } from '@helpers';
import { config } from '@terminal/config/app.config';
import { TeamsGuard } from '@terminal/config/TeamsGuard';
import { useNavigateToAssetOverview } from '@terminal/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useNavigateToThemeOverview } from '@terminal/hooks/use-navigate-to-theme-overview/useNavigateToThemeOverview';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { TabsType } from '@terminal/modules/tab-content/types';
import { useCatalystInsightsList } from '@terminal/react-query-hooks/insights/useCatalystInsights';
import { useCompanyTheme } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { useGetThemePerformance } from '@terminal/react-query-hooks/themes/useGetThemePerformance';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { priceChangeColors } from '@terminal/utils/common/utils';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import { formatPercentageNumber } from '@terminal/widgets/earnings/utils/utils';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import { useThemeDrawer } from '@terminal/widgets/theme-widget/theme-drawer/useThemeDrawer';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { SectionBase } from '../shared/section-base/SectionBase';
import { ThemeCatalystsCell } from './ThemeCatalystsCell';
import styles from './ThemeKnowledgeGraph.module.scss';

type ThemeExposureTableRow = ThemeExposure & {
  priceKey: ThemeExposure['theme'];
  catalystKey: ThemeExposure['theme'];
};

export interface ThemeKnowledgeGraphProps {
  entityTag: string;
  source?: PosthogTrackingSource;
  isInDrawer?: boolean;
}

export const ThemeKnowledgeGraph = ({
  entityTag,
  source,
  isInDrawer = false,
}: ThemeKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const { navigateToPage } = useNavigateToAssetOverview();
  const { openDrawer } = useThemeDrawer();
  const { navigateToThemeOverview } = useNavigateToThemeOverview();
  const { trackThemeOverviewDrawerOpen } = usePosthogEvents();
  const drawerOpenSource = source ?? POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW;

  const {
    data = [],
    isSuccess: isThemeSuccess,
    isFetching,
    isError: isThemeError,
  } = useCompanyTheme({
    entityTag,
    limit: 3,
  });

  const themeIds = data.map(d => d.theme.id);
  const listParams =
    themeIds.length > 0 ? { ids: themeIds.join(',') } : undefined;

  const hasThemes = themeIds.length > 0;

  const {
    themes: themePerformanceThemes,
    isFetching: isThemePerformanceFetching,
    isError: isThemePerformanceError,
  } = useGetThemePerformance(listParams, hasThemes);

  const shouldShowMarketCatalysts = useMemo(
    () => themeIds.length > 0,
    [themeIds]
  );
  const { data: catalystInsights = [], isFetching: isCatalystFetching } =
    useCatalystInsightsList({ themes: themeIds }, shouldShowMarketCatalysts);

  const navigateToKnowledgeGraph = () => {
    navigateToPage({
      entityTag,
      source: drawerOpenSource,
      tab: TabsType.Connections,
      searchParams: getKnowledgeGraphSearchParams({
        entityTag,
        searchParams,
        subtab: KnowledgeGraphTabType.ThemeExposure,
      }),
    });
  };

  const onClickHandler = isInDrawer
    ? (themeId: string) => {
        navigateToThemeOverview(themeId, drawerOpenSource);
      }
    : (themeId: string) => {
        trackThemeOverviewDrawerOpen(drawerOpenSource, [themeId]);
        openDrawer(themeId);
      };

  const themePerformanceMap = useMemo(
    () =>
      new Map<string, ThemePerformanceBasket>(
        themePerformanceThemes?.map(theme => [theme.id, theme]) ?? []
      ),
    [themePerformanceThemes]
  );

  const catalystInsightsMap = useMemo(() => {
    const map = new Map<string, CatalystInsight[]>();
    catalystInsights.forEach(insight => {
      const existing = map.get(insight.identifier) ?? [];
      map.set(insight.identifier, [...existing, insight]);
    });
    return map;
  }, [catalystInsights]);

  const marketCatalystColumn:
    | SmartTableColumn<ThemeExposureTableRow>
    | undefined = useMemo(
    () =>
      shouldShowMarketCatalysts
        ? {
            label: '',
            key: 'catalystKey',
            alignment: 'right',
            render: value => {
              const themeCatalystInsights =
                catalystInsightsMap.get(value.id) ?? [];

              return (
                <ThemeCatalystsCell
                  catalystInsights={themeCatalystInsights}
                  source={drawerOpenSource}
                />
              );
            },
          }
        : undefined,
    [shouldShowMarketCatalysts, catalystInsightsMap, drawerOpenSource]
  );

  const tableColumns: SmartTableColumn<ThemeExposureTableRow>[] = useMemo(
    () => [
      {
        label: t('widget:assetOverview.knowledgeGraphRevamp.columns.theme'),
        key: 'theme',
        render: value => (
          <div className={styles.themeNameWrapper}>
            <ThemeLogoIcon
              name={value.name}
              logo_url={value.logo_url}
              fallbackIcon={
                <div className={styles.logoWrapper}>{value.name.charAt(0)}</div>
              }
            />
            <span className={styles.themeName}>{value.name}</span>
          </div>
        ),
      },
      {
        label: t('widget:assetOverview.knowledgeGraphRevamp.columns.change'),
        key: 'priceKey',
        alignment: 'right',
        render: value => {
          const themePerformance = themePerformanceMap.get(value.id);
          const headlineHorizon =
            themePerformance?.intraday_metadata.headline_horizon;
          const intradayReturn =
            (headlineHorizon
              ? themePerformance?.performance?.[headlineHorizon]
              : themePerformance?.performance?.['1d']) ?? undefined;
          const status = getPriceStatus(intradayReturn);

          return (
            <div className={styles.changeRow}>
              <WithNAFallback
                check={intradayReturn !== undefined}
                value={
                  <span
                    className={styles.changeValue}
                    style={{ color: priceChangeColors[status] }}
                  >
                    {formatPercentageNumber({
                      value: intradayReturn,
                      includePercentSign: true,
                      addPlus: true,
                    })}
                  </span>
                }
              />
            </div>
          );
        },
      },
    ],
    [t, themePerformanceMap]
  );

  return (
    <TeamsGuard>
      <SectionBase<ThemeExposureTableRow>
        title={t('widget:assetOverview.knowledgeGraphRevamp.themeExposure')}
        isError={isThemeError || isThemePerformanceError}
        isEmpty={isThemeSuccess && data.length === 0}
        emptyTitle={t(
          'widget:assetOverview.knowledgeGraphRevamp.empty.themeExposure'
        )}
        tableData={data.map(d => ({
          ...d,
          key: d.theme.id,
          priceKey: d.theme,
          catalystKey: d.theme,
        }))}
        tableColumns={[
          ...tableColumns,
          ...(marketCatalystColumn ? [marketCatalystColumn] : []),
        ]}
        isFetching={
          isFetching || isThemePerformanceFetching || isCatalystFetching
        }
        onExpand={navigateToKnowledgeGraph}
        onRowClick={item => onClickHandler(item.theme.id)}
        hideExpandButton={config.app.isTeamsApp}
      />
    </TeamsGuard>
  );
};
